<script setup lang="ts">
import { computed } from '#imports';
import { UiIconButton, UiIconTemplate, UiIconName } from '../icon';
import { UiSize } from '../../styles/Styles';
import type { QaID } from '../../utils/types';

const props = withDefaults(defineProps<{
  locale?: string;
  tooltip?: string;
  tooltipIcon?: string;
  qa?: QaID;
  indeterminate?: boolean;
  size?: UiSize;
  dark?: boolean;
  disabled?: boolean;
  padding?: boolean;
  fullWidth?: boolean;
  invert?: boolean;
}>(), {
  locale: '',
  tooltip: '',
  tooltipIcon: UiIconName.UserInterface_Question,
  qa: '',
  indeterminate: false,
  size: UiSize.Medium,
  dark: false,
  disabled: false,
  padding: true,
  fullWidth: false,
  invert: false,
});

const modelValue = defineModel<boolean>({ default: false });

const localValue = computed(() => (props.invert ? !modelValue.value : modelValue.value));

function onClick() {
  if (!props.disabled) modelValue.value = !modelValue.value;
}

defineSlots<{
  left?(): void;
  right?(): void;
}>();
</script>

<template lang="pug">
.ui-checkbox-container(:class="[size, disabled && '_disabled', dark && '_dark', padding && '_padding', fullWidth && '_full_width']")
  .ui-checkbox.flex.row-8(:data-qa="qa + locale" @click="onClick")
    .icon
      UiIconTemplate(v-if="indeterminate" :colored="true" :name="UiIconName.CheckboxIndeterminate")
      UiIconTemplate(v-else-if="disabled" :colored="true" :name="localValue ? UiIconName.CheckboxDisabledCheck : UiIconName.CheckboxDisabled")
      UiIconTemplate(v-else :colored="true" :name="localValue ? UiIconName.CheckboxSelected : UiIconName.CheckboxDeselected")
    slot(name="left")
    .label(v-if="locale") {{ $t(locale) }}
    slot(name="right")
    UiIconButton.tooltip.m-l-8(v-if="tooltip" :name="tooltipIcon" :tooltip="tooltip" :padding="size !== UiSize.Small" @click.stop)
</template>

<style lang="scss" scoped>
@import '../../styles/colors';

.icon {
  float: left;
  flex-shrink: 0;
  user-select: none;
}

.label {
  display: flex;
  align-items: center;
  color: $ui-color-text-default;
  user-select: none;
  line-height: 20px;
}

.ui-checkbox-container {
  display: flex;
  max-height: 52px;
  align-items: flex-end;
  white-space: nowrap;

  .ui-checkbox {
    min-height: 32px;
    cursor: pointer;
    align-items: center;
  }

  &.large {
    .ui-checkbox {
      min-height: 48px;
      font-weight: 700;
      font-size: 16px;
      line-height: 20px;
    }
    .icon {
      --uno: s-28;
    }
  }

  &.medium {
    .ui-checkbox {
      min-height: 32px;
      font-size: 14px;
      font-weight: 500;
    }
    .icon {
      --uno: s-20;
    }
  }

  &.small {
    .ui-checkbox {
      min-height: 20px;
      font-size: 13px;
    }
    .icon {
      --uno: s-20;
    }
  }

  &._disabled {
    .ui-checkbox {
      cursor: not-allowed;
    }

    .tooltip {
      color: #a4a7b4;
    }
  }

  &:not(._padding) {
    .ui-checkbox {
      min-height: 20px;
    }
  }

  &._dark {
    .label {
      color: #ffffff;
    }
  }

  &._full_width {
    .ui-checkbox {
      width: 100%;

      .label {
        width: 100%;
      }
    }
  }
}
</style>
